/**
 * The card every Rowted Admin page is made of — §4.2's "rebuilt on `Card`".
 *
 * Title, an optional count, a rule under it, an optional control on the right,
 * and rows of a fixed height. It is the office app's `<RecordCard>` said again
 * for a screen that has no record page to hang one off, and it exists for the
 * same reason: the markup below was written out nine times across four admin
 * screens before this file, and two of them had already drifted — one header
 * `items-baseline gap-2`, another not, so the count beside a title sat a pixel
 * lower on Health than on Settings.
 *
 * A page that wants a different card does not get one. That is the bargain:
 * somebody arriving at their fourth admin screen should find the same shape,
 * and the way to keep that true is for there to be one of it.
 */
import type { ReactNode } from 'react'
import { Card, SectionLabel, Tag, type TagTone } from '../../components/primitives.js'
import { cn } from '../../lib/cn.js'

export function AdminPanel({
  title, count, note, action, footer, children, className,
}: {
  title: string
  /** Shown after the title as `· 3`, the way the office app's cards do it. */
  count?: number
  /** One quiet phrase beside the title — what the card is of, not what it says. */
  note?: string
  /** Exactly one control on the right. Two would be a toolbar. */
  action?: ReactNode
  footer?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={cn('p-0', className)}>
      <header className="flex items-center gap-2 border-b border-line-soft px-4 py-3">
        <b className="text-card">
          {title}
          {count !== undefined && <span className="tnum text-text-muted"> · {count}</span>}
        </b>
        {note && <span className="min-w-0 truncate text-[11.5px] text-text-faint">{note}</span>}
        {action && <span className="ml-auto shrink-0">{action}</span>}
      </header>

      {children}

      {footer && (
        <div className="border-t border-line-soft px-4 py-2 text-[11px] text-text-faint">
          {footer}
        </div>
      )}
    </Card>
  )
}

/**
 * A row inside one — the 44px-ish `px-4 py-3` row, and its last-child rule.
 *
 * Not *every* list row on an admin screen: the role blurbs, the security facts
 * and the webhook counts are a label over a sentence at `py-2.5`, which is a
 * different thing from a three-column row and stays that way. This is the row
 * that was written out identically in `settings.tsx` and `health.tsx`.
 */
export function AdminRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <li className={cn('flex items-start gap-3 border-b border-line-soft px-4 py-3 last:border-b-0',
      className)}>
      {children}
    </li>
  )
}

/** The `<ul>` the rows go in, so no page has to remember to strip its bullets. */
export function AdminRows({ children }: { children: ReactNode }) {
  return <ul className="m-0 list-none p-0">{children}</ul>
}

/**
 * A label over a number, in the shape the Health page's queue counters use.
 *
 * The label is `<SectionLabel>` rather than the four classes it is made of:
 * the Health page spelled them out with `tracking-[0.05em]` and this page
 * with `0.06em`, which is §3.2's range read twice and two counters that do
 * not line up beside each other.
 *
 * `bad` is a tone, not a colour: the number turns red and the label does not,
 * because a red label reads as a category of problem rather than as one
 * number that is wrong.
 */
export function AdminStat({ label, value, bad, sub }: {
  label: string
  value: ReactNode
  bad?: boolean
  sub?: string
}) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <SectionLabel>{label}</SectionLabel>
      {/* 20px is §3.2's KPI number. The Health page had these at 17px, which
          was nothing but a number typed twice. */}
      <span className={cn('tnum text-[20px] font-bold leading-none',
        bad ? 'text-danger' : 'text-text')}>
        {value}
      </span>
      {sub && <span className="truncate text-[11.5px] text-text-faint">{sub}</span>}
    </span>
  )
}

/** A label, a value and an optional word — the About row of an admin card. */
export function AdminFact({ label, value, tone, tag }: {
  label: string
  value: ReactNode
  tone?: TagTone
  tag?: string
}) {
  return (
    <AdminRow>
      <span className="w-[140px] shrink-0 text-cell text-text-muted">{label}</span>
      <span className="min-w-0 flex-1 text-cell text-text">{value}</span>
      {tag && <Tag tone={tone} className="shrink-0">{tag}</Tag>}
    </AdminRow>
  )
}
